import type { BizyAssistantMessageNode } from '../types/nodes';

// ─── applyNodeToken ──────────────────────────────────────────────────
//
// Seq-keyed token buffer. The cable can deliver `node_token` events out
// of order on reconnect / packet reordering, so we collect by seq and
// re-derive the ordered content on every tick.

export type NodeTokenBuffer = Map<number, string>;

export function applyNodeToken(buffer: NodeTokenBuffer, seq: number, content: string): { buffer: NodeTokenBuffer; orderedContent: string } {
	const next = new Map(buffer);
	next.set(seq, content);

	const orderedContent = [...next.entries()]
		.sort(([a], [b]) => a - b)
		.map(([, c]) => c)
		.join('');

	return { buffer: next, orderedContent };
}

// ─── bakeDocument ────────────────────────────────────────────────────
//
// On `complete`, assemble the final persisted node list from what streamed.
// Two shapes, selected by `preserveStreamedDocument`:
//
//   • Incremental synthesis (default): the answer streamed as token deltas
//     (`node_token`) plus optional cards. `content` is the authoritative
//     final text, so we rebuild a single leading markdown node from it and
//     keep the non-markdown cards. The streamed markdown nodes were just the
//     rendering shadow of `content` and are discarded.
//
//   • Typed document (Data Agent): a halting agent streamed a multi-node
//     Document (scorecard → chart → narration → follow-ups → source_details)
//     whole via `node_appended`. Here ORDER and the interspersed markdown
//     matter, and `content` would only duplicate the narration — so we keep
//     the streamed nodes verbatim, dropping only the live-only `working_plan`
//     markdown (a planning scratchpad, not part of the answer).

export function bakeDocument(documentNodes: BizyAssistantMessageNode[], finalContent: string, preserveStreamedDocument = false): BizyAssistantMessageNode[] {
	if (preserveStreamedDocument) {
		return documentNodes.filter((n) => !(n.type === 'markdown' && n.payload.role === 'working_plan'));
	}

	const trimmed = finalContent.trim();
	const cards = documentNodes.filter((n) => n.type !== 'markdown');

	if (trimmed.length === 0) {
		return cards;
	}

	return [{ type: 'markdown', payload: { content: finalContent } }, ...cards];
}
